import { useCallback, useRef, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';

import type { Process } from '../../../../bridge/types/process.ts';

const isVisibleJobStatus = (status: Process['status'] | undefined) =>
	status === 'active' || status === 'paused';

// A row that stands for a job inside a container is gated on the JOB's status,
// never the container's. Pause writes the artifact (artifact_types.py:117-120),
// so a container check would report a paused monitor as active and would hide a
// live monitor whose campaign is paused.
const jobStatus = (job: Process) => job.job_artifact?.status ?? job.status;

export const useActiveJobs = () => {
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();
	const [jobs, setJobs] = useState<Process[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	// Flips false→true once the first fetch settles. Lets the widget tell a
	// genuine first load (show skeletons) apart from a background refetch over
	// data we already have (keep the current rows, no skeleton re-flash).
	const hasLoadedOnce = useRef(false);

	// Each call bumps this and captures its own id. A response only writes state
	// if its id is still the latest, so a slow earlier request can't clobber a
	// faster later one. Mirrors the guard in useAudiences.ts.
	const requestIdRef = useRef(0);

	const refresh = useCallback(async () => {
		if (!userId) return;

		const requestId = ++requestIdRef.current;
		setLoading(true);
		setError(false);
		try {
			const res = await bridgewayApi.get<Process[]>(
				`/api/user/${userId}/processes/?kind=job`,
			);
			if (requestId === requestIdRef.current) {
				setJobs(
					Array.isArray(res)
						? res.filter((job) => isVisibleJobStatus(jobStatus(job)))
						: [],
				);
			}
		} catch (err) {
			if (requestId !== requestIdRef.current) return;
			logger.error('useActiveJobs refresh failed', err);
			setError(true);
		} finally {
			if (requestId === requestIdRef.current) {
				hasLoadedOnce.current = true;
				setLoading(false);
			}
		}
	}, [userId, bridgewayApi]);

	return {
		jobs,
		loading,
		error,
		hasLoadedOnce: hasLoadedOnce.current,
		refresh,
	};
};
